import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup, Sheet, SheetContent, SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Sparkles } from 'lucide-react';
import { Outlet, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/authContext';
import { useFeatureFlag } from '../../../contexts/featureFlagContext';
import { OnlyOfficeConnectorProvider } from '../../../contexts/OnlyOfficeConnectorContext';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { useProposalRecordShellEnabled } from '../../RecordShell/recordShellFlag';
import { AISidebarShell } from './AISidebarShell';
import { DevResyncBanner } from './DevResyncBanner';
import { ProposalV2Provider, useProposalV2Context } from './ProposalV2Context';
import { ConfirmDialogProvider } from './shared/ConfirmDialog';
import { WorkspaceProvider } from './workspace/WorkspaceContext';
/**
 * V2 proposal dashboard shell.
 *
 * Two-pane layout: main content (left, contains the nested route's <Outlet />)
 * + right AI sidebar. The left navigation rail is the global `Sidebar.tsx`
 * (proposal v2 is the only proposal UI since OD-60): the proposal record rail
 * (`ProposalSidebar`) under `growth_record_shell_v1`, else `ProjectNavItems` —
 * we don't render a second rail here.
 *
 * Under the record shell the AI pane is the page's one assistant: its open
 * state is the layout store's `assistOpen` (topbar Assist button, ⌘J), and the
 * "Ask AI" pills are replaced by one mobile-only Assist button (FND-20).
 *
 * Responsive behavior:
 *  - Desktop (md and up): the AI sidebar is a `ResizablePanel` you can drag.
 *  - Narrow (< md):       the AI sidebar becomes a slide-in `Sheet` overlay,
 *                          and the main pane gets the full width.
 *
 * This is a layout component used at the `proposal/:id/v2` router level. The
 * nested children (HomePage, ComplianceHubPage, etc.) render via <Outlet />.
 */ export function ProposalDashboardV2() {
    const { id } = useParams();
    const { user } = useAuth();
    const chatStorageKey = `proposal-chat::${user?.id ?? 'anonymous'}::${id ?? ''}`;
    return /*#__PURE__*/ _jsx(ConfirmDialogProvider, {
        children: /*#__PURE__*/ _jsx(ProposalV2Provider, {
            storageKey: chatStorageKey,
            children: /*#__PURE__*/ _jsx(OnlyOfficeConnectorProvider, {
                children: /*#__PURE__*/ _jsx(WorkspaceProvider, {
                    projectId: id ?? '',
                    children: /*#__PURE__*/ _jsx("div", {
                        "data-testid": Testids.project.proposalDashboard,
                        className: "contents",
                        children: /*#__PURE__*/ _jsx(ProposalDashboardV2Shell, {})
                    })
                })
            })
        }, chatStorageKey)
    });
}
function ProposalDashboardV2Shell() {
    const { sidebarOpen, setSidebarOpen, toggleSidebar, dockedAssist, activeChatId } = useProposalV2Context();
    const isDesktop = useMediaQuery('(min-width: 768px)');
    const shell = useProposalRecordShellEnabled();
    const { pathname } = useLocation();
    const isVolumeRoute = pathname.includes('/v2/volume/');
    // Record shell: the page surface token; flag off keeps today's slate.
    const pageBg = shell ? 'bg-[color:var(--surface-page)]' : 'bg-slate-50';
    // WRT-11: on volume routes the docked Assist tab is the one assistant.
    const writingWorkspace = useFeatureFlag('writing_workspace_v1') === true;
    const hidePills = writingWorkspace && isVolumeRoute;
    const hidePane = writingWorkspace && dockedAssist;
    const aiSize = shell && isVolumeRoute ? 25 : 30;
    // Narrow viewport: render the main pane full-width and put the AI sidebar in a Sheet.
    if (!isDesktop) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "relative flex h-full w-full flex-col",
            children: [
                /*#__PURE__*/ _jsx(DevResyncBanner, {}),
                /*#__PURE__*/ _jsx("div", {
                    className: `min-h-0 flex-1 w-full overflow-auto ${pageBg}`,
                    children: /*#__PURE__*/ _jsx(Outlet, {})
                }),
                shell ? // The topbar Assist button is hidden below sm; this is the same single state.
                /*#__PURE__*/ _jsx(SpecraButton, {
                    className: "fixed bottom-4 right-4 z-20 sm:hidden",
                    iconLeft: /*#__PURE__*/ _jsx(Sparkles, {
                        "aria-hidden": "true"
                    }),
                    onClick: toggleSidebar,
                    size: "sm",
                    variant: "outline",
                    children: "Assist"
                }) : hidePills ? null : // aria-label kept: ui-cli journeys click "Open AI sidebar".
                /*#__PURE__*/ _jsx(SpecraButton, {
                    "aria-label": "Open AI sidebar",
                    className: "fixed bottom-4 right-4 z-20 rounded-[var(--radius-pill)] shadow-lg",
                    iconLeft: /*#__PURE__*/ _jsx(Sparkles, {
                        "aria-hidden": "true"
                    }),
                    onClick: toggleSidebar,
                    size: "sm",
                    variant: "outline",
                    children: "Ask AI"
                }),
                /*#__PURE__*/ _jsx(Sheet, {
                    open: sidebarOpen && !hidePane,
                    onOpenChange: setSidebarOpen,
                    children: /*#__PURE__*/ _jsx(SheetContent, {
                        side: "right",
                        // Override sheet defaults: full width up to a sane max, and zero
                        // padding so AISidebarShell controls its own layout.
                        className: "w-[92vw] max-w-md border-0 p-0 sm:!max-w-md",
                        children: /*#__PURE__*/ _jsx(AISidebarShell, {
                            embedded: true
                        }, activeChatId)
                    })
                })
            ]
        });
    }
    // Desktop layout: resizable two-pane.
    return /*#__PURE__*/ _jsxs("div", {
        className: "relative flex h-full w-full flex-col",
        children: [
            /*#__PURE__*/ _jsx(DevResyncBanner, {}),
            !sidebarOpen && !shell && !hidePills && /*#__PURE__*/ _jsx("div", {
                className: "flex shrink-0 justify-end px-4 py-2",
                children: /*#__PURE__*/ _jsx(SpecraButton, {
                    "aria-label": "Open AI sidebar",
                    className: "rounded-[var(--radius-pill)]",
                    iconLeft: /*#__PURE__*/ _jsx(Sparkles, {
                        "aria-hidden": "true"
                    }),
                    onClick: toggleSidebar,
                    size: "sm",
                    variant: "outline",
                    children: "Ask AI"
                })
            }),
            /*#__PURE__*/ _jsxs(ResizablePanelGroup, {
                direction: "horizontal",
                className: "min-h-0 flex-1",
                children: [
                    /*#__PURE__*/ _jsx(ResizablePanel, {
                        defaultSize: sidebarOpen && !hidePane ? 100 - aiSize : 100,
                        minSize: 40,
                        children: /*#__PURE__*/ _jsx("div", {
                            className: `h-full overflow-auto ${pageBg}`,
                            children: /*#__PURE__*/ _jsx(Outlet, {})
                        })
                    }),
                    sidebarOpen && !hidePane && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(ResizableHandle, {
                                withHandle: true
                            }),
                            /*#__PURE__*/ _jsx(ResizablePanel, {
                                defaultSize: aiSize,
                                minSize: 20,
                                maxSize: 50,
                                children: /*#__PURE__*/ _jsx(AISidebarShell, {}, activeChatId)
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
